import { type ReactNode, createElement } from 'react';
import { WidgetElement, TUNNEL_HOST_STYLES, parseCsvNumbers, parseEnum } from '../core';
import type { BookingFilterConfig, BookingTunnelProps } from '@cfixe-monorepo/shared-components/booking/types';
import type { TunnelMode } from '@cfixe-monorepo/shared-components/booking/components/shared/TunnelModeSwitch';
import { REDIRECT_RETURN_PARAM } from '@cfixe-monorepo/shared-components/pro-payment/redirect-return';
import { parsePreselectParam } from '@cfixe-monorepo/shared-components/booking/cart/resolve-cart-entry';
import { BookingRoot } from './mount-booking';
// Tunnel de réservation (bt-*) + UI paiement Stripe (pp-* : bouton payer, erreurs). Bundlés inline par
// Vite (?inline renvoie une string), injectés dans le shadow root. Sans pro-payment.scss, le bouton
// « Confirmer la réservation » se rendait avec un cadenas SVG pleine largeur et sans aucun style.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — handled by Vite plugin
import bookingStyles from '@cfixe-monorepo/shared-components/booking/styles/booking-tunnel.scss?inline';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — handled by Vite plugin
import proPaymentStyles from '@cfixe-monorepo/shared-components/pro-payment/styles/pro-payment.scss?inline';
// Gestion de la réservation (BookingManageView) affichée sur l'écran de succès : tunnel course, et tunnel
// classique en `success-mode="manage-inline"` (défaut).
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — handled by Vite plugin
import manageViewStyles from '@cfixe-monorepo/shared-components/booking/styles/booking-manage-view.scss?inline';

const ELEMENT_TAG = 'propelsia-booking';

type BookingSuccessMode = NonNullable<BookingTunnelProps['successMode']>;
/* Écran de succès du tunnel classique. Gestion intégrée par défaut (spec Parcours § 10) : le lien vers la
   page de gestion menait à une 404 sur le domaine du site hôte. */
const SUCCESS_MODES: readonly BookingSuccessMode[] = ['manage-inline', 'message'];
const DEFAULT_SUCCESS_MODE: BookingSuccessMode = 'manage-inline';

/**
 * Custom Element entry for the Propelsia booking widget. Same offer as the `/book/[slug]` link:
 * « Prestations | Trajet » tabs when the pro offers both, ride tunnel alone for a ride-only pro.
 *
 * Attributes (in addition to the standard theme attributes from widget-core):
 *  - widget-id (required)         Pro public widget UUID (or legacy slug)
 *  - lang                         Locale (default: fr-FR)
 *  - mode                         'inline' (default) or 'modal'
 *  - prestation-ids               Comma-separated prestation IDs filter
 *  - place-ids                    Comma-separated pro_place IDs filter
 *  - member-ids                   Comma-separated team_member IDs filter
 *  - category-ids                 Comma-separated catalog category IDs filter
 *  - preselect-ids                Comma-separated prestations to pre-add to cart, each `id[:price_id[:quantity]]`
 *                                 (a single complete one opens the express journey: slot, then checkout)
 *  - tab                          Tab opened first when the pro offers both: 'booking' or 'ride'
 *                                 (default: ride, as the /book link; same role as its `?mode=`)
 *  - code                         Promo code or gift card code applied as soon as the cart is ready
 *                                 (default: the host page's `?code=`; its `?promo=` is applied too)
 *  - success-mode                 Success screen: 'manage-inline' (default, « Gérer mon rendez-vous » opens
 *                                 rescheduling and cancellation in place) or 'message' (confirmation only,
 *                                 linking to the management page hosted by Propelsia)
 *  (filters, code and success-mode apply to the classic tunnel only, not to the ride tunnel)
 *
 * Events dispatched (bubble + composed):
 *  - propelsia:booking:completed  detail: { reservationUuid, tunnel: 'booking' | 'ride' }
 *  - propelsia:booking:step-change detail: { step, previousStep, step_index, step_total } (classic tunnel only;
 *                                 step_index / step_total: rank of the step and number of steps of this
 *                                 customer's journey, confirmation not counted, absent on the confirmation)
 *  - propelsia:booking:error      detail: { code, message }
 */
export class PropelsiaBookingElement extends WidgetElement {
  static get observedAttributes(): readonly string[] {
    return [
      'widget-id',
      'slug',
      'lang',
      'mode',
      'prestation-ids',
      'place-ids',
      'member-ids',
      'category-ids',
      'preselect-ids',
      'tab',
      'code',
      'success-mode',
      'accent',
      'bg',
      'text',
      'radius',
      'font',
    ];
  }

  protected override getModalTriggerLabel(): string {
    return 'Réserver en ligne';
  }

  protected override getRedirectReturnParams(): readonly string[] {
    return [REDIRECT_RETURN_PARAM.BOOKING, REDIRECT_RETURN_PARAM.RIDE];
  }

  protected override injectStyles(shadow: ShadowRoot): void {
    const style = document.createElement('style');
    style.textContent = `
      ${TUNNEL_HOST_STYLES}
      ${bookingStyles as string}
      ${proPaymentStyles as string}
      ${manageViewStyles as string}
    `;
    shadow.appendChild(style);
  }

  private buildFilters(): BookingFilterConfig {
    return {
      prestation_ids: parseCsvNumbers(this.getAttribute('prestation-ids')),
      place_ids: parseCsvNumbers(this.getAttribute('place-ids')),
      member_ids: parseCsvNumbers(this.getAttribute('member-ids')),
      category_ids: parseCsvNumbers(this.getAttribute('category-ids')),
      preselect_lines: parsePreselectParam(this.getAttribute('preselect-ids')),
    };
  }

  protected override getRootComponent(): ReactNode {
    const identifier = this.getAttribute('widget-id') ?? this.getAttribute('slug') ?? '';
    if (!identifier) {
      const error = document.createElement('div');
      error.textContent = 'Propelsia booking: missing required attribute "widget-id"';
      return createElement('div', { style: { padding: 16, color: '#c00' } }, error.textContent);
    }
    const locale = this.getAttribute('lang') ?? 'fr-FR';
    const tab = this.getAttribute('tab');
    const successMode = parseEnum(this.getAttribute('success-mode')?.trim(), SUCCESS_MODES, DEFAULT_SUCCESS_MODE);

    return createElement(BookingRoot, {
      // Another pro: fresh profile, tab and tunnels.
      key: identifier,
      slug: identifier,
      locale,
      filters: this.buildFilters(),
      claimedRedirectReturn: this.claimedRedirectReturn,
      requestedMode: tab === 'booking' || tab === 'ride' ? tab : undefined,
      prefilledCode: this.getAttribute('code')?.trim() || undefined,
      successMode,
      onCompleted: (uuid: string, tunnel: TunnelMode): void => {
        this.emit('propelsia:booking:completed', { reservationUuid: uuid, tunnel });
        // Modale : fermeture automatique peu après la confirmation, une fois l'écran de succès vu — jamais
        // quand cet écran porte la gestion du rendez-vous (tunnel classique en `manage-inline`) : elle se
        // fermerait pendant un report ou une annulation.
        const managesInline = tunnel === 'booking' && successMode === 'manage-inline';
        if (this.getMode() === 'modal' && !managesInline) {
          window.setTimeout((): void => this.closeModal(), 6000);
        }
      },
      onStepChange: (step: string, info: { previousStep?: string; stepIndex?: number; stepTotal?: number }): void => {
        this.emit('propelsia:booking:step-change', {
          step,
          previousStep: info.previousStep,
          step_index: info.stepIndex,
          step_total: info.stepTotal,
        });
      },
      onError: (code: string, message: string): void => {
        this.emit('propelsia:booking:error', { code, message });
      },
    });
  }
}

if (typeof customElements !== 'undefined' && !customElements.get(ELEMENT_TAG)) {
  customElements.define(ELEMENT_TAG, PropelsiaBookingElement);
}
